import { useState, useEffect, useCallback } from 'react';

const useInfiniteScroll = ({
    fetchFunction,
    pageSize = 10,
    dependencies = [],
    scrollOffset = 50
}) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [pageNumber, setPageNumber] = useState(1);

    const fetchData = useCallback(async (page = 1, append = false) => {
        console.log(`Fetching page ${page}, append: ${append}, loading: ${loading}`);
        if (loading) return;
        setLoading(true);
        
        try {
            const result = await fetchFunction(page, pageSize);
            console.log('Fetch result:', result);
            
            if (result && !result.error) {
                const newData = result.data || [];
                console.log(`Got ${newData.length} items for page ${page}`);
                setData(prev => append ? [...prev, ...newData] : newData);
                setHasMore(newData.length === pageSize);
                if (append) {
                    setPageNumber(page);
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [fetchFunction, pageSize]);

    const loadMore = useCallback(() => {
        if (hasMore && !loading) {
            const nextPage = pageNumber + 1;
            fetchData(nextPage, true);
        }
    }, [hasMore, loading, pageNumber, fetchData]);

    const resetData = useCallback(() => {
        setData([]);
        setPageNumber(1);
        setHasMore(true);
        setLoading(false);
    }, []);

    const refetchData = useCallback(() => {
        resetData();
        setTimeout(() => {
            fetchData(1, false);
        }, 0);
    }, [resetData, fetchData]);

    // Auto load more on scroll
    useEffect(() => {
        let timeoutId;
        
        const handleScroll = () => {
            // Debounce scroll events for better performance on desktop
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                const windowHeight = window.innerHeight || document.documentElement.clientHeight;
                const documentHeight = Math.max(
                    document.body.scrollHeight,
                    document.body.offsetHeight,
                    document.documentElement.clientHeight,
                    document.documentElement.scrollHeight,
                    document.documentElement.offsetHeight
                );
                
                // More aggressive loading for desktop - 70% threshold
                const scrollPercentage = (scrollTop + windowHeight) / documentHeight;
                const shouldLoad = scrollPercentage >= 0.7;
                
                console.log('Scroll detected:', { 
                    scrollTop, 
                    windowHeight, 
                    documentHeight,
                    scrollPercentage: (scrollPercentage * 100).toFixed(1) + '%',
                    shouldLoad,
                    hasMore, 
                    loading,
                    userAgent: navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop'
                });
                
                if (shouldLoad && hasMore && !loading) {
                    console.log('Loading more data...');
                    loadMore();
                }
            }, 50); // 50ms debounce for desktop scroll events
        };

        // Add both scroll and wheel events for better desktop coverage
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('wheel', handleScroll, { passive: true });
        
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('wheel', handleScroll);
        };
    }, [hasMore, loading, loadMore]);

    // Initial fetch and refetch on dependencies change
    useEffect(() => {
        console.log('Dependencies changed:', dependencies);
        if (dependencies.length > 0 || dependencies.length === 0) {
            console.log('Triggering refetch...');
            refetchData();
        }
    }, dependencies);

    return {
        data,
        loading,
        hasMore,
        pageNumber,
        loadMore,
        refetchData,
        resetData
    };
};

export default useInfiniteScroll;